import { Center, IconButton, InputRightElement } from '@chakra-ui/react';
import { ReactComponent as CloseIcon } from 'assets/icons/close_circle.svg';
import { memo, useCallback } from 'react';

import { AutoCompleteInputClearButtonProps } from './AutoCompleteInput.types';
import { useAutoCompleteContext } from './AutoCompleteInputContext';

/**
 * AUTOCOMPLETE CLEAR BUTTON
 *
 * Renders a clear button with [x] icon that will clear the autocomplete input when clicked.
 * - Button is rendered on right side of input element.
 * - Must be contained within AutoCompleteInputGroup component
 *
 * Usage:
 * <AutoCompleteInput>
 *  <AutoCompleteInputGroup>
 *   <AutoCompleteClearButton />
 *  </AutoCompleteInputGroup>
 * </AutoCompleteInput>
 */
export const AutoCompleteClearButton = memo(({ onClearInputEnd }: AutoCompleteInputClearButtonProps) => {
  const { onClearInput } = useAutoCompleteContext();

  const onClick = useCallback(() => {
    onClearInput();
    onClearInputEnd?.();
  }, [onClearInput, onClearInputEnd]);

  return (
    <InputRightElement h={'full'}>
      <IconButton
        size='xs'
        borderRadius='full'
        variant='unstyled'
        onClick={onClick}
        aria-label={'Clear input'}
        icon={
          <Center>
            <CloseIcon stroke='var(--vg-colors-outline-gray)' />
          </Center>
        }
        data-testid='clear-autocomplete-input-button'
      />
    </InputRightElement>
  );
});

AutoCompleteClearButton.displayName = 'AutoCompleteClearButton';
